/**
 * The shell and the state machine.
 *
 *   starting -> signed-out -> choosing -> locked -> unlocked
 *      |           |                                   ^  |
 *      |           +------------- invited -------------+  |
 *      |                                                  |
 *      +--------> picking >---------------------------->--+
 *                    ^                                    |
 *                    +------------ idle, or "Switch" -----+
 *
 * `picking` is the account picker, and it is reached two ways: on arrival at
 * a browser that already knows somebody, and from an open session that has
 * gone quiet or been handed over deliberately. It is NOT the same as
 * `locked`, which has always meant "signed in, but this device has no family
 * key". Two different locks, on two different things -- see `Phase`.
 *
 * SIGNED IN and UNLOCKED are not the same thing. Cognito proves to the API who
 * you are; the family code proves to this device that you can read what the
 * API stores. The server enforces the first and has no way to enforce the
 * second, which is exactly the property the design is built on -- a
 * compromised API hands an attacker opaque bytes.
 *
 * `invited` is the link path: somebody arrived at `/join#code=...` while
 * signed out. The code was taken out of the address bar and stashed before
 * React rendered (see `family/join.ts`), so all this state does is explain
 * where they are and send them through the ordinary sign-up. The stash
 * survives that round trip the same way the PKCE verifier does, and the
 * effect below picks it up when they come back.
 *
 * Copy here is parent-facing throughout. "Directed to children" is itself a
 * COPPA trigger, so even though children use the product, the words are
 * addressed to the adult holding the account.
 */

import { Loading } from './components/Spinner';
import { BackdropProvider, ValleyBackdrop } from './brand/backdrop';
import { BrandLogo } from './brand/GoalPicture';
import { useCallback, useEffect, useState } from 'react';
import {
  completeSignIn,
  currentAccount,
  forgetDeletedAccount,
  lock,
  noteAccount,
  restore,
  signedInEmail,
  signIn,
  signInWithApple,
  signInWithGoogle,
  googleUsedHere,
  signOut,
  signUp,
  takeAccountNotice,
} from './auth/cognito';
import { config } from './config';
import { watchIdle } from './auth/idle';
import { forgetAllPins, lockedAccounts } from './auth/pin';
import { childSessionCount } from './child/session';
import { AccountPicker } from './screens/AccountPicker';
import { ChildPage } from './screens/ChildPage';
import { FullValley } from './screens/FullValley';
import { forgetValleyMode, inValleyMode, rememberValleyMode } from './valley/mode';
import { ApiError } from './api/client';
import {
  ensureSetUp,
  joinFamily,
  listFamilies,
  mintFamilyCode,
  hasNoCode,
  previewFamily,
  type FamilyPreview,
  type FamilySummary,
} from './family/families';
import { clearJoinCode, stashedJoinCode } from './family/join';
import { appSide, native } from './native/platform';
import { forgetEverything, recallKey } from './crypto/vault';
import { ChooseFamily } from './screens/ChooseFamily';
import { Home, type Tab } from './screens/Home';
import { Unlock } from './screens/Unlock';
import { FamilyCodeBanner } from './screens/FamilyCodeBanner';
import { loadState, WrongFamilyKey, type FamilyState } from './family/state';
import { noteOwnEmail } from './family/memberDevices';
import { myKeyRequest } from './family/keyRequests';
import { SignInScreen } from './screens/SignInScreen';

type Phase =
  | 'starting'
  | 'signed-out'
  /** Birbly's own sign-in / sign-up form (/sign-in, /sign-up): no browser. */
  | 'auth'
  /**
   * The account picker: this browser knows somebody, but no session is open.
   *
   * Distinct from `signed-out`, which is for a browser that has never been
   * used. Showing "Create an account" to a parent whose PIN is two taps away
   * is the wrong first thing to say, and showing the picker to somebody
   * arriving for the first time would be a list with nothing in it.
   */
  | 'picking'
  /**
   * The valley, full screen, left running on this machine.
   *
   * Not a modal over `unlocked` but a phase of its own, because it has to
   * survive a reload -- see `valley/mode`. A parent who props a laptop in the
   * kitchen and knocks F5 should get the valley back, not the ledger.
   */
  | 'valley'
  | 'invited'
  | 'choosing'
  /** Signed in, but this device has no family key. Not the PIN lock. */
  | 'locked'
  | 'unlocked'
  | 'error';

export function App() {
  const [phase, setPhase] = useState<Phase>('starting');
  const [authStart, setAuthStart] = useState<'sign-in' | 'sign-up'>('sign-in');
  const [message, setMessage] = useState('');
  /**
   * One line above the front page or the picker: "you were signed out",
   * "this account was deleted". Read once from sessionStorage, so it survives
   * the reload that signing out does and is gone after it has been seen.
   */
  const [notice, setNotice] = useState<string | null>(null);
  const [family, setFamily] = useState<FamilySummary | null>(null);
  const [familyKey, setFamilyKey] = useState<CryptoKey | null>(null);
  // Null until a key has opened the family. `false` means the API will refuse
  // every mutation until setup has run once.
  const [ready, setReady] = useState<boolean | null>(null);
  const [familyName, setFamilyName] = useState('');
  /**
   * Whether this family has a family code yet. Null until we know.
   *
   * Kept beside the summary rather than read off `keyWrap` every time,
   * because the two answers come from different places: `keyWrap` is what
   * the sign-in list said, and this also has to be true the moment a family
   * is created, joined or migrated in this session, when no sign-in list has
   * been fetched since.
   */
  const [hasCode, setHasCode] = useState<boolean | null>(null);
  /**
   * A code to show the parent once, in a banner, on the way into the valley.
   * Set on creating a family and on migrating one. Never shown twice -- it
   * lives in Settings from then on, which is the whole point of it.
   */
  const [freshCode, setFreshCode] = useState<string | null>(null);
  // Carried from Home rather than fetched again: the roster is already
  // decrypted there, and the journal needs it to put names on rows.
  /**
   * Which section of the parent area is showing. Kept HERE rather than in
   * Home so it survives a detour -- a child's page, the full valley -- and a
   * parent comes back to the section they left.
   */
  const [tab, setTab] = useState<Tab>('home');
  const [settingsFocus, setSettingsFocus] = useState<string | null>(null);
  /** Set while a stashed code is being confirmed with the person. */
  const [invitation, setInvitation] = useState<FamilyPreview | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * The child whose page a parent is looking at, and the state it opens on.
   * Carried from Home rather than fetched again for the same reason the
   * journal is -- the roster, goals and entries are already decrypted there.
   * ChildPage keeps itself current from that starting point.
   */
  const [previewing, setPreviewing] = useState<
    { state: FamilyState; personId: string } | null
  >(null);
  /**
   * What the full valley opens on. Set when Home launches it, and re-fetched
   * on a cold start because a reload has no Home to carry it from.
   */
  const [valleyState, setValleyState] = useState<FamilyState | null>(null);

  const settle = useCallback(async (): Promise<FamilySummary | null> => {
    const families = await listFamilies();
    if (families.length === 0) {
      setFamily(null);
      setPhase('choosing');
      return null;
    }
    // One family today. The API returns a set because a parent serving several
    // is a later feature, and the shape is already right for it.
    const first = families[0]!;
    setFamily(first);
    setHasCode(!hasNoCode(first));
    // No key here, but this device may have asked another to let it in and
    // been told yes while signed out: collect it now (family/keyRequests.ts).
    const remembered =
      (await recallKey(first.id)) ??
      (await myKeyRequest(first.id)
        .then((mine) => (mine?.status === 'approved' ? mine.key : null))
        .catch(() => null));
    if (remembered) {
      setFamilyKey(remembered);
      // A machine left as a valley screen comes back as one. Without this a
      // reload is a way past the PIN behind the sun and moon.
      setPhase(inValleyMode() ? 'valley' : 'unlocked');
    } else {
      setPhase('locked');
    }
    return first;
  }, []);

  /**
   * The API answers 410 when the token outlived its account: deleted on
   * another device, or by support, while this one still held a session. An
   * access token is checked without asking Cognito, so it keeps working for up
   * to an hour after the account is gone -- the API's tombstone is what
   * catches it. Drop the session and the PIN record, and say so on the front
   * page (or the picker, if other accounts are saved here).
   */
  const goneAccount = useCallback((problem: unknown): boolean => {
    if (!(problem instanceof ApiError) || problem.status !== 410) return false;
    forgetDeletedAccount();
    setFamily(null);
    setFamilyKey(null);
    setNotice(takeAccountNotice());
    setPhase(knownHere() ? 'picking' : 'signed-out');
    return true;
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function start(): Promise<void> {
      try {
        // A callback lands here with ?code=; anything else tries the refresh
        // token left in sessionStorage by an earlier sign-in this session.
        const session = (await completeSignIn()) ?? (await restore());
        if (cancelled) return;
        // Who signed in last on this browser. This used to wipe every family
        // key whenever it changed, so a new parent could not inherit keys for
        // a family they might not be in. Keys are kept PER ACCOUNT now (see
        // crypto/vault.ts), which settles that without locking out the parent
        // who was here first.
        if (session) noteAccount(session);

        const formPath = window.location.pathname.replace(/\/+$/, '');
        if (formPath === '/sign-in' || formPath === '/sign-up') {
          if (!session) {
            setAuthStart(formPath === '/sign-up' ? 'sign-up' : 'sign-in');
            setPhase('auth');
            return;
          }
          window.history.replaceState({}, '', '/');
        }

        if (!session) {
          // Arrived by link but not signed in yet. Say where they are and
          // send them through sign-up; the stashed code is waiting when they
          // come back to this same effect with a session.
          //
          // An invitation still wins over the picker. Somebody following a
          // link is trying to join a family, and answering that with a list
          // of other people's accounts loses the thing they clicked.
          if (stashedJoinCode()) {
            setPhase('invited');
            return;
          }
          setNotice(takeAccountNotice());
          setPhase(knownHere() ? 'picking' : 'signed-out');
          return;
        }

        const stashed = stashedJoinCode();
        if (stashed) {
          const handled = await arrive(stashed);
          if (cancelled) return;
          if (handled) return;
        }
        await settle();
      } catch (problem) {
        if (cancelled) return;
        if (goneAccount(problem)) return;
        setMessage(
          problem instanceof ApiError || problem instanceof Error
            ? problem.message
            : 'Something went wrong.',
        );
        setPhase('error');
      }
    }

    /**
     * A stashed code, with a session in hand. Returns true if it took over.
     *
     * Already a member of that family? Skip the confirmation -- it would be
     * asking somebody whether they want to join a family they are in -- and
     * go straight through. That is the same call either way: joining and
     * unlocking are one route, see `joinFamily`.
     */
    async function arrive(code: string): Promise<boolean> {
      try {
        const [preview, mine] = await Promise.all([
          previewFamily(code),
          listFamilies(),
        ]);
        if (cancelled) return true;
        if (mine.some((one) => one.id === preview.id)) {
          const opened = await joinFamily(code);
          if (cancelled) return true;
          clearJoinCode();
          setFamily(mine.find((one) => one.id === preview.id) ?? { id: opened.id });
          setHasCode(true);
          setFamilyKey(opened.key);
          setPhase('unlocked');
          return true;
        }
        // The phase stays where it is. The confirmation below renders on
        // `invitation` alone, and whichever way they answer it calls
        // `settle()`, which is what decides the phase.
        setInvitation(preview);
        return true;
      } catch (problem) {
        // A code that is wrong, rotated out or rate limited. Drop it and let
        // them in the ordinary way rather than stranding them on a dead link.
        clearJoinCode();
        setMessage(problem instanceof Error ? problem.message : '');
        return false;
      }
    }

    void start();
    return () => {
      cancelled = true;
    };
  }, [settle]);

  // Has setup run? One read, and it decides what follows unlocking. Asking
  // here rather than inside Home keeps "is this family set up" out of the
  // screen that already assumes it is.
  useEffect(() => {
    if (phase !== 'unlocked' || !family || !familyKey || ready !== null) return;
    let cancelled = false;
    void loadState(family.id, familyKey)
      .then(async (state) => {
        if (cancelled) return;
        setFamilyName(state.familyName);
        // Put this parent's own address back on the members list if a
        // start-over cleared it. Once a session; never in the way.
        void noteOwnEmail(family.id, familyKey);
        if (state.initialized) {
          setReady(true);
          return;
        }
        // A create whose setup call did not land. It used to be a screen
        // asking for the family name a second time and for two amounts;
        // both are ordinary settings now, so it runs itself. A 409 means
        // another parent got there first, which is the answer this wanted.
        await ensureSetUp(family.id, familyKey, state.familyName);
        if (!cancelled) setReady(true);
      })
      .catch((problem: unknown) => {
        if (cancelled) return;
        // The family started over with a new key: this one is forgotten
        // already, so ask for the new code.
        if (problem instanceof WrongFamilyKey) {
          setFamilyKey(null);
          setPhase('locked');
          return;
        }
        // A family that cannot be read is one this device has no key for.
        // Let Home surface the real error rather than guessing here.
        setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [phase, family, familyKey, ready]);

  /**
   * A cold start straight into the valley has no Home to carry state from.
   *
   * Only on that path: when Home launches the valley it hands the state over
   * already decrypted, and this does nothing. A reload of a kitchen screen
   * has nothing to hand over, so it reads once here.
   */
  useEffect(() => {
    if (phase !== 'valley' || !family || !familyKey || valleyState) return;
    let cancelled = false;
    void loadState(family.id, familyKey)
      .then((state) => {
        if (!cancelled) setValleyState(state);
      })
      .catch(() => {
        // Nothing to show and no way to say so on a screen with no chrome.
        // Fall back to the ordinary app, which has an error path.
        if (!cancelled) {
          forgetValleyMode();
          setPhase('unlocked');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [phase, family, familyKey, valleyState]);

  /**
   * Give a family made before family codes one, from this unlocked device.
   *
   * It can only happen here. The wrapping has to be made under a code the
   * server chooses, by something holding the family key -- and the server has
   * never been able to hold that. So the first unlocked device to load a
   * family with no `keyWrap` does it, and shows the parent the code once.
   */
  useEffect(() => {
    if (phase !== 'unlocked' || !family || !familyKey || !ready) return;
    if (hasCode !== false) return;
    let cancelled = false;
    // Set before the call, not after. This effect re-runs on every render
    // that touches its dependencies, and a second run would mint a second
    // code and retire the one just shown to the parent.
    setHasCode(true);
    void mintFamilyCode(family.id, familyKey, familyName)
      .then((code) => {
        if (cancelled) return;
        setFreshCode(code);
      })
      .catch(() => {
        // Not worth interrupting anybody over: the family carries on working
        // exactly as it did, and the next page load tries again.
        if (!cancelled) setHasCode(false);
      });
    return () => {
      cancelled = true;
    };
  }, [phase, family, familyKey, ready, familyName, hasCode]);

  /**
   * Sign out, and LEAVE the family key where it is.
   *
   * These are two different things and conflating them cost a parent their
   * access. Signed in is a property of the SESSION; unlocked is a property of
   * the DEVICE. Ending the first should not end the second.
   *
   * The old behaviour wiped every key on sign-out, reasoning that the next
   * person to use this browser would otherwise inherit it. They would inherit
   * something useless: the family key decrypts ciphertext, and fetching any
   * ciphertext needs a Cognito session they do not have. Key without session
   * reads nothing.
   *
   * Forgetting a device is now its own deliberate act, in Settings.
   */
  /**
   * End the session but keep the account, and show the picker.
   *
   * The idle timer calls this, and so does "Switch account". Everything the
   * family key opened is dropped from React state at the same time -- not
   * because it is dangerous in memory, but because leaving it set would let
   * a screen render a family the current session no longer has.
   */
  const forgetOpenFamily = useCallback(() => {
    setPreviewing(null);
    setValleyState(null);
    setTab('home');
    setFamilyKey(null);
    setFamily(null);
    setReady(null);
  }, []);

  /** The account Switch just locked, so the picker's Back can resume it. */
  const [leftAccount, setLeftAccount] = useState<string | null>(null);

  const toPicker = useCallback(() => {
    setLeftAccount(currentAccount()?.sub ?? null);
    lock();
    forgetOpenFamily();
    setPhase('picking');
  }, [forgetOpenFamily]);

  // There is deliberately no "show the picker but stay signed in" variant.
  // An earlier version had one, reasoning that a parent who taps Switch and
  // changes their mind should not pay a PIN to get back. That is the wrong
  // trade on a family machine: "switch account" is a handover, and a handover
  // that leaves the previous session alive is the thing the lock exists to
  // prevent. Switching locks, exactly as going idle does.

  /**
   * Lock after a quiet spell, closing the gap DECISIONS.md §19 left open.
   *
   * Mounted for the life of the app rather than gated on a phase, and that is
   * deliberate. Gating it on `unlocked` left one real hole: a parent who
   * pressed "Switch", saw the picker and then walked away still had a live
   * session, and nothing was watching it. `watchIdle` re-checks on every fire
   * and refuses to lock an account that has no PIN or no session at all, so
   * running it everywhere costs one timer and closes that.
   */
  useEffect(() => {
    // SUSPENDED while the valley is up. An ambient display that signs itself
    // out after ten quiet minutes is not an ambient display -- going quiet is
    // the whole point of it. What guards the account there is the PIN behind
    // the sun and moon, which is a gate a person has to walk through rather
    // than a timer nobody sees.
    if (phase === 'valley') return;
    return watchIdle(toPicker);
  }, [toPicker, phase]);

  const leave = useCallback(() => {
    signOut();
  }, []);

  /**
   * For a shared or borrowed machine: sign out AND forget the key here.
   *
   * Every lock goes too. "Forget this device" that left other parents' PINs
   * on it would be a strange kind of forgetting -- the next person would
   * still be offered a list of this household's accounts on a machine
   * somebody has just declared they are finished with.
   */
  const leaveAndForget = useCallback(() => {
    forgetAllPins();
    void forgetEverything().finally(() => signOut());
  }, []);

  const goTo = useCallback((next: Tab, focus?: string) => {
    setTab(next);
    // Settings takes a section to jump to; Goals takes a list (and maybe
    // one goal) -- see family/goals.ts `readFocus`.
    setSettingsFocus(next === 'settings' || next === 'goals' ? (focus ?? null) : null);
    // A new section starts at its top, not wherever the last one was.
    window.scrollTo?.({ top: 0 });
  }, []);

  if (phase === 'starting') return <Shell><Loading fill label="Opening Birbly…" /></Shell>;

  if (phase === 'picking') {
    return (
      <Shell onHome={() => setPhase('signed-out')}>
        <AccountNotice text={notice} />
        <AccountPicker
          onBack={() => setPhase('signed-out')}
          // Back from Switch is back to the dashboard: the PIN for the
          // account that was open, then straight in (2026-10-13).
          {...(leftAccount ? { resumeSub: leftAccount } : {})}
          onResumed={() => {
            // A different account may have just been opened, so whatever the
            // previous one had decrypted has to go before `settle` decides
            // which family this session can see.
            forgetOpenFamily();
            setPhase('starting');
            void settle().catch((problem: unknown) => {
              if (!goneAccount(problem)) setPhase('error');
            });
          }}
          onUseAnother={() => void signIn({ fresh: true })}
          onCreateAccount={() => void signUp()}
        />
      </Shell>
    );
  }

  if (phase === 'auth') {
    return (
      <Shell>
        <AccountNotice text={notice} />
        <SignInScreen
          start={authStart}
          social={<SocialButtons />}
          onBack={() => {
            window.history.replaceState({}, '', '/');
            setPhase(stashedJoinCode() ? 'invited' : knownHere() ? 'picking' : 'signed-out');
          }}
        />
      </Shell>
    );
  }

  if (phase === 'error') {
    return (
      <Shell>
        <p className="error">{message}</p>
        <button className="primary" onClick={() => void signIn()}>Try again</button>
      </Shell>
    );
  }

  if (phase === 'invited') {
    return (
      <Shell>
        <h2>You&rsquo;ve been invited to a family on Birbly</h2>
        <p>
          Birbly keeps a family&rsquo;s activity fund: the money they put away,
          the goals they set, and a painted valley that gets greener as they
          save.
        </p>
        <p>
          Make an account and you will be taken straight to the family that
          sent you this link &mdash; you will not need to type a code.
        </p>
        <div className="actions stacked">
          <button className="primary full" onClick={() => void signUp()}>
            Create an account
          </button>
          <SocialButtons />
          <button className="text-button" onClick={() => void signIn()}>
            I already have one
          </button>
        </div>
        <p className="footnote">
          Sign up with an email address{config.cognito.google ? ' or your Google account' : ''}.
          It is the only thing we ask for, and it stays in your sign-in account &mdash; nothing in your family&rsquo;s
          data is stored against it in a form we can read.
        </p>
      </Shell>
    );
  }

  // Signed in, holding a code for a family they are not in yet. One
  // confirmation, because joining means reading somebody's whole ledger and
  // a membership is much harder to undo than a question is to ask.
  if (invitation) {
    return (
      <Shell>
        <h2>
          {invitation.family ? `Join ${invitation.family}?` : 'Join this family?'}
        </h2>
        <p>
          Joining lets you see this family&rsquo;s money, goals and journal, so
          it is worth a second look before you do.
        </p>
        {message ? <p className="error">{message}</p> : null}
        <div className="actions">
          <button aria-busy={busy}
            className="primary"
            disabled={busy}
            onClick={() => {
              const code = stashedJoinCode();
              if (!code) {
                setInvitation(null);
                void settle().catch(() => setPhase('error'));
                return;
              }
              setBusy(true);
              setMessage('');
              void joinFamily(code)
                .then((joined) => {
                  clearJoinCode();
                  setInvitation(null);
                  setFamily({ id: joined.id });
                  setHasCode(true);
                  setFamilyKey(joined.key);
                  setPhase('unlocked');
                })
                .catch((problem: unknown) => {
                  setMessage(
                    problem instanceof Error ? problem.message : 'Something went wrong.',
                  );
                })
                .finally(() => setBusy(false));
            }}
          >
            {busy ? 'Working…' : 'Yes, join it'}
          </button>
          <button
            disabled={busy}
            onClick={() => {
              // Cleared whether or not it worked, so a dead link does not
              // greet them again on every reload for the rest of the session.
              clearJoinCode();
              setInvitation(null);
              setMessage('');
              void settle().catch((problem: unknown) => {
                setMessage(
                  problem instanceof Error ? problem.message : 'Something went wrong.',
                );
                setPhase('error');
              });
            }}
          >
            Not now
          </button>
        </div>
      </Shell>
    );
  }

  if (phase === 'signed-out') {
    return (
      <Shell>
        {/* The panel opened with a paragraph and no heading -- the only screen
            in the app that did -- and that paragraph began "Valley keeps your
            family's activity fund", which is what the header two lines above
            already says. So the first thing a parent read was the second time
            they had read it. The heading now carries the idea the header does
            not: that this is a fund a CHILD can see. */}
        <AccountNotice text={notice} />
        <h2>A fund your children can see</h2>
        <p>
          Record what the family puts away, set goals together, and keep the
          journal. A painted valley gets greener as you save &mdash; which is
          the part a child actually reads.
        </p>
        {/* Stacked, not side by side. `.actions` makes its buttons equal
            width, which is right when the choice is balanced and wrong here:
            almost everybody arriving at this screen is new, and the one who
            is not is looking for a link rather than weighing two options. */}
        <div className="actions stacked">
          <button className="primary full" onClick={() => void signUp()}>
            Create an account
          </button>
          <SocialButtons />
          <button className="text-button" onClick={() => void signIn()}>
            I already have one
          </button>
          {/* A child with a code from a grown-up, on a device that opened the
              parents' side: their own page is one tap away. */}
          {/* Children have their own app; the parents' app does not offer
              their page (2026-10-19). On the website both sides are here. */}
          {appSide() === 'parents' ? null : (
            <button className="text-button" onClick={() => window.location.assign('/me')}>
              I&rsquo;m a kid with a code
            </button>
          )}
          {/* The way back to the picker, now that the picker has a way here. */}
          {knownHere() ? (
            <button className="text-button" onClick={() => setPhase('picking')}>
              Choose a saved account
            </button>
          ) : null}
        </div>
        <p className="footnote">
          Sign up with an email address{config.cognito.google ? ' or your Google account' : ''}.
          It is the only thing we ask for, and it stays in your sign-in account &mdash; nothing in your family&rsquo;s
          data is stored against it in a form we can read.
        </p>
      </Shell>
    );
  }

  if (phase === 'choosing') {
    return (
      <Shell>
        {message ? (
          <p className="error">
            That invitation link did not work &mdash; {message} You can still
            join with the family code below, or start a family of your own.
          </p>
        ) : null}
        <ChooseFamily
          onReady={(id, key, newCode) => {
            setFamily({ id });
            setHasCode(true);
            setFamilyKey(key);
            if (newCode) setFreshCode(newCode);
            setPhase('unlocked');
          }}
        />
      </Shell>
    );
  }

  if (phase === 'locked' && family) {
    return (
      <Shell>
        <Unlock
          family={family}
          onUnlocked={(key) => {
            setFamilyKey(key);
            setPhase('unlocked');
          }}
          onSignOut={leave}
        />
      </Shell>
    );
  }

  if (phase === 'unlocked' && ready === null) {
    return <Shell><Loading fill label="Opening your family…" /></Shell>;
  }

  // Shown once, over the valley, and never again -- Settings has it from now
  // on. That is the difference between this and the key phrase it replaced: there
  // is nothing here anybody has to keep.
  if (freshCode && family) {
    return (
      <Shell>
        <FamilyCodeBanner
          code={freshCode}
          familyName={familyName}
          onDone={() => setFreshCode(null)}
        />
      </Shell>
    );
  }

  // Before everything, and outside the Shell: the valley is the whole screen,
  // with no header, no panel and no way to the account except the sun.
  if (phase === 'valley' && family && familyKey) {
    if (!valleyState) {
      return <Shell><Loading fill label="Opening the valley…" /></Shell>;
    }
    return (
      <FullValley
        familyId={family.id}
        familyKey={familyKey}
        initial={valleyState}
        onExit={() => {
          forgetValleyMode();
          void leaveFullScreen();
          setPhase('unlocked');
        }}
        onForgotPin={() => {
          // Out of the valley FIRST. Cognito redirects away and back, and a
          // mode flag still set on return would land them in the valley
          // again -- the exact screen they said they could not leave.
          forgetValleyMode();
          void leaveFullScreen();
          void signIn();
        }}
        onLockedOut={() => {
          // Five wrong PINs destroyed the record. Out of the valley and out
          // of the session -- the way back is a full sign-in, which is what
          // the cap means everywhere else.
          forgetValleyMode();
          void leaveFullScreen();
          toPicker();
        }}
      />
    );
  }

  // Ahead of Settings and the journal: a parent who tapped a child's name is
  // looking at that, and the screens below are reached from Home the same way.
  if (previewing && family && familyKey) {
    return (
      // The child's own full-screen layout, not the narrow sign-in panel: a
      // child's page is a page (2026-10-13). See ChildPage.
      <ChildPage
        familyId={family.id}
        familyKey={familyKey}
        initial={previewing.state}
        personId={previewing.personId}
        onBack={() => setPreviewing(null)}
        onTab={(next) => {
          setPreviewing(null);
          goTo(next);
        }}
      />
    );
  }

  if (family && familyKey) {
    return (
      <Shell onHome={() => goTo('home')} bare family={familyName}>
        <Home
          familyId={family.id}
          familyKey={familyKey}
          tab={tab}
          onTab={goTo}
          settingsFocus={settingsFocus}
          onRenamed={setFamilyName}
          onForgetDevice={leaveAndForget}
          onChildPage={(state, personId) => setPreviewing({ state, personId })}
          onFullValley={(state) => {
            setValleyState(state);
            rememberValleyMode();
            void goFullScreen();
            setPhase('valley');
          }}
          onSignOut={leave}
          onSwitchAccount={toPicker}
        />
      </Shell>
    );
  }

  // Reached when the phase and the data disagree -- `locked` with no family,
  // `unlocked` with no key. It used to say "One moment." and mean it forever:
  // no button, no explanation, nothing to do but clear the browser.
  //
  // The cause is fixed above, so this should not happen. "Should not happen"
  // is exactly the screen that needs a way out, because the one thing worse
  // than an error is an error that looks like a spinner.
  return (
    <Shell>
      <h2>Something did not line up</h2>
      <p>
        You are signed in, but this device could not work out which family to
        show. Signing out and back in usually settles it.
      </p>
      <div className="actions">
        <button className="primary" onClick={leave}>Sign out</button>
        <button onClick={leaveAndForget}>Sign out and forget this device</button>
      </div>
    </Shell>
  );
}

/**
 * Does this browser already know somebody?
 *
 * Either a parent who left a PIN or a child who linked a device. Read fresh
 * rather than held in state: both live in localStorage and either can be
 * written by the other tab on this machine, which is a normal thing to happen
 * on the computer this app is designed for.
 */
/**
 * Ask the browser for the whole screen, and give it back.
 *
 * Best effort on purpose, and never awaited anywhere that matters. A
 * fullscreen request needs a user gesture and is refused outright on iOS
 * Safari, inside some embedded webviews, and whenever a permission policy
 * says no. A valley in an ordinary window is still a valley; a valley that
 * threw an unhandled rejection because a browser said no is not.
 */
async function goFullScreen(): Promise<void> {
  try {
    await document.documentElement.requestFullscreen?.();
  } catch {
    /* A window is fine. */
  }
}

async function leaveFullScreen(): Promise<void> {
  try {
    if (document.fullscreenElement) await document.exitFullscreen?.();
  } catch {
    /* Nothing to do; the phase has already changed. */
  }
}

/** "Signed out" or "deleted", above whatever the parent sees next. */
function AccountNotice({ text }: { text: string | null }) {
  return text ? (
    <p className="sign-in-note" role="status">
      {text}
    </p>
  ) : null;
}

function knownHere(): boolean {
  return lockedAccounts().length > 0 || childSessionCount() > 0;
}

function Shell({
  children,
  onSwitchAccount,
  onHome,
  bare = false,
  family,
}: {
  children: React.ReactNode;
  /** Makes the logo a button to this app's front page for the screen's state. */
  onHome?: () => void;
  /** The family on screen, so a parent in two never wonders which this is. */
  family?: string;
  /**
   * No surrounding card. The parent sections are cards of their own, and
   * wrapping them in another one was a card inside a card -- extra borders
   * and padding on a phone, for nothing.
   */
  bare?: boolean;
  /**
   * Turns the email in the corner into a way out of this account.
   *
   * Passed only by the screens a parent reaches once they are in, because on
   * the picker itself it would be a button back to where they already are,
   * and on the sign-in screens there is no account to leave.
   */
  onSwitchAccount?: () => void;
}) {
  /**
   * WHOSE ACCOUNT THIS IS. Read here rather than threaded down from App,
   * because every screen renders through this component and the alternative
   * was the same prop on a dozen call sites.
   *
   * Reading module state during render is only safe because of what this
   * value is: it changes when a session begins or ends, both of which already
   * force a render -- a phase transition or a navigation away. It is never
   * stale on screen.
   *
   * Why it is here at all: signing out deliberately leaves the family key on
   * the device, so two parents share a browser by design. Until this line
   * existed there was nothing on any screen that said which of them the app
   * thought you were, and the bug where that went wrong showed up as a
   * permanent "One moment." rather than as the wrong name in the corner.
   */
  const account = signedInEmail();
  return (
    <BackdropProvider>
      <main className={bare ? 'shell shell-parent' : 'shell shell-plain'}>
        {/* The painting behind every screen. Living Valley, 2026-10-11. */}
        <ValleyBackdrop />
        <header className="shell-top">
          <div className="shell-brand">
            <h1 className="visually-hidden">Birbly</h1>
            {/* The logo goes home, as a logo does everywhere else. Before
                2026-10-13 it was a picture, so the picker -- reached by
                Switch, with no Back -- was a dead end. */}
            {onHome ? (
              <button type="button" className="shell-home" onClick={onHome} aria-label="Birbly home">
                <BrandLogo reversed height={46} className="shell-logo" alt="" />
              </button>
            ) : (
              <BrandLogo reversed height={46} className="shell-logo" alt="" />
            )}
            {family ? null : <p className="eyebrow shell-tagline">A family activity fund</p>}
          </div>
          {family || account ? (
            <div className="shell-who">
              {family ? <p className="shell-family">{family}</p> : null}
              {account ? (
                onSwitchAccount ? (
                  <button
                    type="button"
                    className="account account-switch"
                    title={`Signed in as ${account}. Switch account.`}
                    aria-label={`Signed in as ${account}. Switch account.`}
                    onClick={onSwitchAccount}
                  >
                    <span className="account-initial" aria-hidden="true">
                      {account.trim().charAt(0).toUpperCase()}
                    </span>
                    <span className="account-email">{account}</span>
                    <span className="account-swap">Switch</span>
                  </button>
                ) : family ? (
                  // Switch lives in Settings, above Sign out (2026-10-13); the
                  // corner just says who this is.
                  <p className="account account-switch account-badge" title={`Signed in as ${account}`}>
                    <span className="account-initial" aria-hidden="true">
                      {account.trim().charAt(0).toUpperCase()}
                    </span>
                    <span className="visually-hidden">Signed in as {account}</span>
                  </p>
                ) : (
                  <p className="account" title={account}>
                    {account}
                  </p>
                )
              ) : null}
            </div>
          ) : null}
        </header>
        {bare ? <div className="sections">{children}</div> : <section className="panel">{children}</section>}
        <footer className="legal-links">
          <a href="/privacy">Privacy</a> · <a href="/terms">Terms</a>
        </footer>
      </main>
    </BackdropProvider>
  );
}

/**
 * "Continue with Google": signs a parent in, or signs them up, through
 * Cognito with Google as the identity broker. Shown only when the pool has
 * Google configured (VITE_COGNITO_GOOGLE). The mark is inline, so the button
 * makes no request of its own -- the first contact with Google is the
 * redirect a parent chose.
 */
function SocialButtons() {
  // The App Store requires Sign in with Apple in an iOS app that offers
  // Google (guideline 4.8). Inside the iOS app, Google only appears beside
  // Apple; anywhere else each button follows its own setting.
  const ios = native()?.platform === 'ios';
  const google = config.cognito.google && (!ios || config.cognito.apple);
  return (
    <>
      {config.cognito.apple ? <AppleButton /> : null}
      {google ? <GoogleButton /> : null}
    </>
  );
}

/**
 * "Continue with Apple", in the form Apple's guidelines give: black, the
 * Apple mark, and those words. Inline, like the Google mark, so the button
 * itself fetches nothing.
 */
function AppleButton() {
  return (
    <button className="full apple-button" onClick={() => void signInWithApple()}>
      <svg viewBox="0 0 17 20" width="16" height="19" aria-hidden="true">
        <path fill="currentColor" d="M14.2 10.6c0-2.5 2.1-3.7 2.2-3.8-1.2-1.7-3-2-3.7-2-1.6-.2-3.1.9-3.9.9-.8 0-2-.9-3.4-.9-1.7 0-3.3 1-4.2 2.6-1.8 3.1-.5 7.7 1.3 10.2.9 1.2 1.9 2.6 3.2 2.6 1.3-.1 1.8-.8 3.3-.8 1.6 0 2 .8 3.4.8 1.4 0 2.3-1.3 3.1-2.5 1-1.4 1.4-2.8 1.4-2.9-.1 0-2.7-1-2.7-4.2zM11.7 3.2c.7-.9 1.2-2 1.1-3.2-1 0-2.3.7-3 1.6-.7.8-1.2 2-1.1 3.1 1.1.1 2.3-.6 3-1.5z" />
      </svg>
      Continue with Apple
    </button>
  );
}

function GoogleButton() {
  return (
    <button className="full google-button" onClick={() => void signInWithGoogle()}>
      <svg viewBox="0 0 18 18" width="18" height="18" aria-hidden="true">
        <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
        <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
        <path fill="#FBBC05" d="M3.97 10.72A5.41 5.41 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.05l3.01-2.33z" />
        <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
      </svg>
      {/* Somebody who has used Google here before is continuing; anybody
          else is, as far as this device knows, signing up (2026-10-19). */}
      {googleUsedHere() ? 'Continue with Google' : 'Sign up with Google'}
    </button>
  );
}
